import { normalizeLocale, type AppLocale } from "./i18n/locale-state";
import { resolvePlatformRootDomain } from "./platform-urls";

const DEFAULT_LOCALE_STORAGE_KEY = "airange.locale";
const DEFAULT_COOKIE_NAME = "airange_locale";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function resolveCookieDomain(hostname: string) {
  const normalized = hostname.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  const rootDomain = resolvePlatformRootDomain(normalized);
  if (rootDomain) {
    return `.${rootDomain}`;
  }
  return undefined;
}

function readCookie(name: string) {
  if (typeof document === "undefined") {
    return null;
  }
  const encodedName = `${encodeURIComponent(name)}=`;
  const matched = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(encodedName));
  if (!matched) {
    return null;
  }
  return decodeURIComponent(matched.slice(encodedName.length));
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }
  const cookieParts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  const domain = resolveCookieDomain(window.location.hostname);
  if (domain) {
    cookieParts.push(`domain=${domain}`);
  }
  if (window.location.protocol === "https:") {
    cookieParts.push("Secure");
  }
  document.cookie = cookieParts.join("; ");
}

export function getStoredAppLocale(storageKey = DEFAULT_LOCALE_STORAGE_KEY, cookieName = DEFAULT_COOKIE_NAME): AppLocale {
  if (typeof window === "undefined") {
    return "zh-CN";
  }
  const stored = window.localStorage.getItem(storageKey) ?? readCookie(cookieName) ?? window.navigator.language;
  return normalizeLocale(stored);
}

export function setStoredAppLocale(
  locale: string,
  storageKey = DEFAULT_LOCALE_STORAGE_KEY,
  cookieName = DEFAULT_COOKIE_NAME,
): AppLocale {
  const nextLocale = normalizeLocale(locale);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(storageKey, nextLocale);
  }
  writeCookie(cookieName, nextLocale);
  return nextLocale;
}
